import {useEffect, useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  useResponderPregunta,
  type PreguntaCargo,
  type PreguntaRespuestaTipo,
} from '@/hooks/empresa/perfil_cargo/use-preguntas-cargo'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

/** D7. «Todavía no sé» va a la misma altura: esconderla empuja a inventar un dato. */
const TIPOS: PreguntaRespuestaTipo[] = ['respondida', 'no_aplica', 'no_se']

function validateRespuesta(pregunta: PreguntaCargo, tipo: PreguntaRespuestaTipo, valor: string): string | null {
  if (tipo !== 'respondida') return null
  if (!valor) return 'errors.preguntaRespuestaVacia'
  if (pregunta.opciones.length && !pregunta.opciones.includes(valor)) return 'errors.preguntaOpcionDesconocida'
  return null
}

/** §4 de la spec: sin motivo, un «no aplica» no se distingue de un descuido. */
function validateMotivo(tipo: PreguntaRespuestaTipo, motivo: string): string | null {
  return tipo === 'no_aplica' && !motivo.trim() ? 'errors.preguntaNoAplicaSinMotivo' : null
}

export function PreguntaResponderModal({
  open,
  onOpenChange,
  businessId,
  positionId,
  pregunta,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  positionId: string
  pregunta: PreguntaCargo | null
}) {
  const {t} = useTranslation()
  const responder = useResponderPregunta(businessId, positionId)
  const [tipo, setTipo] = useState<PreguntaRespuestaTipo>('respondida')
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const [errorValor, setErrorValor] = useState<string | undefined>()
  const [errorMotivo, setErrorMotivo] = useState<string | undefined>()
  const reset = responder.reset

  useEffect(() => {
    if (open && pregunta) {
      setTipo(pregunta.respuesta_tipo ?? 'respondida')
      setValor(pregunta.respuesta ?? '')
      setMotivo(pregunta.no_aplica_motivo ?? '')
      setErrorValor(undefined)
      setErrorMotivo(undefined)
      reset()
    }
  }, [open, pregunta, reset])

  if (!pregunta) return null
  const abierta = pregunta

  function blurValor(e: FocusEvent<HTMLInputElement>) {
    setErrorValor(validateRespuesta(abierta, tipo, e.target.value.trim()) ?? undefined)
  }

  function blurMotivo(e: FocusEvent<HTMLTextAreaElement>) {
    setErrorMotivo(validateMotivo(tipo, e.target.value) ?? undefined)
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const limpio = valor.trim()
    const limpioMotivo = motivo.trim()
    const falloValor = validateRespuesta(abierta, tipo, limpio)
    const falloMotivo = validateMotivo(tipo, motivo)
    setErrorValor(falloValor ?? undefined)
    setErrorMotivo(falloMotivo ?? undefined)
    if (falloValor || falloMotivo) return
    responder.mutate(
      {
        preguntaKey: abierta.pregunta_key,
        esDeEmpresa: abierta.ambito === 'empresa',
        respuestaTipo: tipo,
        respuesta: tipo === 'respondida' ? limpio : null,
        motivo: tipo === 'no_aplica' ? limpioMotivo : null,
      },
      {onSuccess: () => onOpenChange(false)}
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{abierta.enunciado}</DialogTitle>
          <DialogDescription>
            {abierta.requisitos.length
              ? t('preguntaCargo.modal.desbloquea', {requisitos: abierta.requisitos.join(', ')})
              : t('preguntaCargo.modal.sinRequisito')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('preguntaCargo.modal.comoContesta')}</legend>
            {TIPOS.map((opcion) => (
              <label key={opcion} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="pregunta-tipo"
                  value={opcion}
                  data-testid={`pregunta-tipo-${opcion}`}
                  checked={tipo === opcion}
                  onChange={() => {
                    setTipo(opcion)
                    setErrorValor(undefined)
                    setErrorMotivo(undefined)
                  }}
                  className="mt-0.5 accent-primary"
                />
                <span>{t(`preguntaCargo.tipos.${opcion}`)}</span>
              </label>
            ))}
          </fieldset>

          {tipo === 'respondida' && abierta.opciones.length > 0 && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{t('preguntaCargo.modal.eligeOpcion')}</legend>
              {abierta.opciones.map((opcion) => (
                <label key={opcion} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="pregunta-opcion"
                    value={opcion}
                    data-testid={`pregunta-opcion-${opcion}`}
                    checked={valor === opcion}
                    onChange={() => {
                      setValor(opcion)
                      setErrorValor(undefined)
                    }}
                    className="mt-0.5 accent-primary"
                  />
                  <span>{t(`preguntaCargo.opciones.${abierta.pregunta_key}.${opcion}`)}</span>
                </label>
              ))}
              <FieldError error={errorValor} />
            </fieldset>
          )}

          {/* Sin `autoFocus`: pelea con el foco de Radix y el salto de scroll se come el clic en los
           * radios de arriba. */}
          {tipo === 'respondida' && abierta.opciones.length === 0 && (
            <div className="space-y-2">
              <Label htmlFor="pregunta-respuesta">{t('preguntaCargo.modal.suRespuesta')}</Label>
              <Input
                maxLength={tope('NOTA')}
                id="pregunta-respuesta"
                data-testid="pregunta-respuesta-input"
                value={valor}
                onChange={(e) => {
                  setValor(e.target.value)
                  setErrorValor(undefined)
                }}
                onBlur={blurValor}
                aria-invalid={!!errorValor}
              />
              <FieldError error={errorValor} />
            </div>
          )}

          {tipo === 'no_aplica' && (
            <div className="space-y-2">
              <Label htmlFor="pregunta-motivo">{t('preguntaCargo.modal.motivo')}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="pregunta-motivo"
                data-testid="pregunta-motivo-input"
                rows={3}
                value={motivo}
                onChange={(e) => {
                  setMotivo(e.target.value)
                  setErrorMotivo(undefined)
                }}
                onBlur={blurMotivo}
                aria-invalid={!!errorMotivo}
              />
              <FieldError error={errorMotivo} />
            </div>
          )}

          <p className="text-xs text-muted-foreground" data-testid="pregunta-modal-constancia">
            {t('preguntaCargo.modal.constancia')}
          </p>
          <FormDialogFooter
            error={responder.isError ? translateApiError(responder.error) : null}
            pending={responder.isPending}
            submitLabel={t('preguntaCargo.modal.guardar')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
